/**
 * Backend DSAR API — POPIA s24 self-service + admin queue hooks.
 *
 * Deletion-type requests record the request only; the F-018 4-eye
 * cryptographic-erasure execution lives elsewhere.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

export type DsarRequestType = 'access' | 'correction' | 'deletion';
export type DsarStatus =
  | 'received'
  | 'identity-verified'
  | 'scoped'
  | 'holds-checked'
  | 'cooling-off'
  /** Transitional state while the F-018 4-of-4 erasure quorum is being
   *  collected (the first erase vote moves cooling-off → awaiting-approvals).
   *  The backend has written this status since F-018 but the union here
   *  lacked it — added with the erase-vote panel (lane A, 2026-07-10). */
  | 'awaiting-approvals'
  | 'fulfilled'
  | 'rejected'
  | 'held-pending-legal-clearance'
  | '4-eye-approved';

/** Subject-access export delivery block — present on access-type requests
 *  once the Information Officer has generated the bundle
 *  (POST /v1/admin/dsar/{id}/export). `url` is a time-limited read-only SAS
 *  link to the JSON bundle; it is `null` in environments where blob
 *  delivery isn't configured, so only render a download affordance when a
 *  live link is present. */
export interface DsarAccessExportView {
  url: string | null;
  expiresAt: string | null;
  sizeBytes: number;
  generatedAt: string;
  sections: string[];
}

export interface DsarRequestView {
  id: string;
  tenantId: string;
  subjectUserId: string;
  requesterUserId: string;
  requestType: DsarRequestType;
  status: DsarStatus;
  reason: string;
  receivedAt: string;
  /** POPIA statutory due date = receivedAt + 30 calendar days. The backend
   *  stamps this at creation and derives it for legacy rows on read, so it is
   *  effectively always present; typed optional/nullable for defensiveness. */
  dueAt?: string | null;
  closedAt?: string;
  assignedToUserId?: string;
  identityProofMethod?: string;
  accessExport?: DsarAccessExportView;
  createdAt: string;
  updatedAt: string;
}

export interface DsarListResponse {
  dsarRequests: DsarRequestView[];
  correlationId: string;
}

export interface DsarGetResponse {
  dsarRequest: DsarRequestView;
  correlationId: string;
}

export function useDsarList() {
  return useQuery<DsarListResponse>({
    queryKey: ['dsar', 'list', 'mine'],
    queryFn: ({ signal }) => api<DsarListResponse>('/api/v1/dsar', { signal }),
  });
}

export function useDsar(id: string | undefined) {
  return useQuery<DsarGetResponse>({
    queryKey: ['dsar', 'detail', id ?? null],
    queryFn: ({ signal }) =>
      api<DsarGetResponse>(`/api/v1/dsar/${encodeURIComponent(id!)}`, { signal }),
    enabled: !!id,
  });
}

export function useCreateDsar() {
  const queryClient = useQueryClient();
  return useMutation<
    DsarGetResponse,
    Error,
    { requestType: DsarRequestType; reason: string }
  >({
    mutationFn: (body) =>
      api<DsarGetResponse>('/api/v1/dsar', { method: 'POST', body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dsar'] });
    },
  });
}

export function useAdminDsarList(opts: { enabled?: boolean } = {}) {
  return useQuery<DsarListResponse>({
    queryKey: ['dsar', 'admin', 'list'],
    queryFn: ({ signal }) => api<DsarListResponse>('/api/v1/admin/dsar', { signal }),
    // Role-gated callers pass enabled:false instead of firing a doomed 403
    // (rm is outside the backend's admin-or-compliance tier — 2026-07-19
    // miss-hunt P1 state-code-firm-01).
    enabled: opts.enabled !== false,
  });
}

export function useAdminDsarPatch() {
  const queryClient = useQueryClient();
  return useMutation<
    DsarGetResponse,
    Error,
    { id: string; patch: Partial<Pick<DsarRequestView, 'status' | 'assignedToUserId' | 'identityProofMethod'>> }
  >({
    mutationFn: ({ id, patch }) =>
      api<DsarGetResponse>(`/api/v1/admin/dsar/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: patch,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dsar'] });
    },
  });
}

// ── build-campaign 2026-07-09 · lane A ───────────────────────────────────────

export interface AdminDsarExportResponse {
  dsarRequest: DsarRequestView;
  accessExport: DsarAccessExportView;
  /** Dev-degrade notice — present when DOCUMENTS_STORAGE_ACCOUNT is unset:
   *  the bundle was assembled + recorded but no blob/SAS was delivered
   *  (`accessExport.url` is null). Render it verbatim; never fake a link. */
  notice?: string;
  correlationId: string;
}

/**
 * Generate the POPIA s23(1) subject-access bundle for an ACCESS-type DSAR.
 * POST /v1/admin/dsar/{id}/export.
 *
 * Contract (handler header): admin/compliance/super_admin; correction/deletion
 * requests 409. Delivery is a 7-day read-only SAS onto the JSON bundle;
 * re-running REPLACES the blob + accessExport block (idempotent regeneration).
 * In environments without blob delivery the backend returns 200 with
 * `accessExport.url: null` + a documented `notice` — NOT an error.
 */
export function useAdminDsarExport() {
  const queryClient = useQueryClient();
  return useMutation<AdminDsarExportResponse, Error, { id: string }>({
    mutationFn: ({ id }) =>
      api<AdminDsarExportResponse>(
        `/api/v1/admin/dsar/${encodeURIComponent(id)}/export`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dsar'] });
    },
  });
}

/** The four distinct approver-role buckets required for the F-018 erasure
 *  quorum (backend REQUIRED_APPROVER_ROLES). Each must be substantiated by
 *  the caller's REAL designation server-side (403 APPROVER_ROLE_MISMATCH):
 *  io → POPIA (deputy-)IO designation · ts → designated MLRO ·
 *  id → compliance role · counsel → counsel role / external-counsel flag. */
export type DsarApproverRole = 'io' | 'ts' | 'id' | 'counsel';

/** Truthful erasure-progress signal (mirrors the backend):
 *  'not-applicable' = still collecting votes · 'approved-pending-destruction'
 *  = 4-of-4 reached but data NOT yet destroyed · 'destroyed' = destruction
 *  confirmed with evidence. */
export type DsarErasureStatus =
  | 'not-applicable'
  | 'approved-pending-destruction'
  | 'destroyed';

export interface DsarEraseVoteResponse {
  dsar: DsarRequestView;
  /** True when all four distinct roles have now approved. */
  quorumReached: boolean;
  /** Total approvals recorded so far (the GET surface does not expose the
   *  approvers array, so this response field is the quorum-progress source). */
  approversCount: number;
  erasureStatus: DsarErasureStatus;
  correlationId: string;
}

/**
 * Cast one approver vote on a deletion-DSAR's cryptographic erasure.
 * POST /v1/dsar/{id}/erase — records the approval; it does NOT destroy data.
 *
 * Contract (handler header): deletion-type only (409 DSAR_NOT_DELETION);
 * status must be cooling-off/awaiting-approvals (409 DSAR_NOT_IN_COOLING_OFF);
 * the 7-day cool-off must have elapsed (409 COOLING_OFF_NOT_COMPLETE /
 * NO_COOLING_OFF_START); four DISTINCT roles, no same-role or same-caller
 * replay (409 DUPLICATE_APPROVER_ROLE); the claimed role must match the
 * caller's real designation (403 APPROVER_ROLE_MISMATCH). Step-up gated.
 */
export function useDsarEraseVote() {
  const queryClient = useQueryClient();
  return useMutation<
    DsarEraseVoteResponse,
    Error,
    { id: string; approverRole: DsarApproverRole; justification: string }
  >({
    mutationFn: ({ id, approverRole, justification }) =>
      api<DsarEraseVoteResponse>(`/api/v1/dsar/${encodeURIComponent(id)}/erase`, {
        method: 'POST',
        body: { approverRole, justification },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dsar'] });
    },
  });
}
